/**
 * Types for the Deckorate "Create slides" wizard. Generation is abstracted
 * behind SlideGenerator, implemented by the real backend client
 * (apiGenerator + slideGenerationApi).
 */

/** Step 1 — where the slides are sourced from. */
export type SlideSource = "text" | "upload";

/**
 * Step 3 — design mode.
 *   "freehand"  AI builds each slide's OOXML from scratch
 *               (POST /create-slide-ai, SSE — service/slideGenerationApi.ts).
 *   "templates" content is fitted into a reviewed layout from the template
 *               library (POST /create-slide-outline then
 *               /create-slide-template — service/templatesApi.ts).
 */
export type DesignMode = "freehand" | "templates";

export type Tone =
  | "Professional"
  | "Conversational"
  | "Persuasive"
  | "Inspirational"
  | "Technical"
  | "Friendly";

export const TONES: readonly Tone[] = [
  "Professional",
  "Conversational",
  "Persuasive",
  "Inspirational",
  "Technical",
  "Friendly",
];

/**
 * A document the user attached on step 2. `chars` drives the "· 7.6k chars"
 * chip; `file` is the real File sent to the backend `/upload` endpoint for
 * server-side text extraction. The File is only ever used inside the dialog
 * (where generation runs) — it never crosses the messageParent boundary.
 */
export interface UploadedDoc {
  name: string;
  chars: number;
  file?: File;
}

/**
 * A finished slide handed from the dialog back to the task pane for insertion.
 * `points`/`title` render the placeholder slide today; `xml` (the backend's
 * `<p:spTree>`) is carried for when real OOXML rendering lands.
 */
export interface GeneratedSlide {
  index: number;
  title: string;
  points: string[];
  /** Slide archetype (mapped from the backend intent_tag) — drives the preview layout. */
  category?: SlideCategory;
  /** Directly-insertable base64 `.pptx` (preferred render path). */
  pptxBase64?: string;
  xml?: string | null;
}

/** Slide archetype (mapped from the backend IntentTag); drives preview layout. */
export type SlideCategory =
  | "COVER"
  | "OVERVIEW"
  | "PROCESS"
  | "COMPARISON"
  | "STATISTICAL"
  | "HIERARCHICAL"
  | "TIMELINE"
  | "CLOSING";

/** Everything the wizard collects, handed to the generator on "Generate". */
export interface CreateSlideConfig {
  source: SlideSource;
  topic: string;
  documents: UploadedDoc[];
  audience: string;
  slideCount: number;
  tone: Tone;
  coverSlide: boolean;
  closingSlide: boolean;
  /** Step 3 choice. "freehand" → the ready create-slide API; "templates" → placeholder. */
  designMode: DesignMode;
  /**
   * Blob storage location of the uploaded document, set once the file has been
   * uploaded to blob (mechanism TBD with backend). When present, generation
   * uses input_type="document"; otherwise it uses the topic.
   */
  blobContainer?: string;
  blobName?: string;
}

export const DEFAULT_CONFIG: CreateSlideConfig = {
  source: "text",
  topic: "",
  documents: [],
  audience: "",
  slideCount: 6,
  tone: "Professional",
  coverSlide: true,
  closingSlide: true,
  designMode: "freehand",
};

export const SLIDE_COUNT_MIN = 3;
export const SLIDE_COUNT_MAX = 20;

/**
 * The wizard config as it crosses the dialog -> pane boundary. Raw `File`
 * objects can't survive `messageParent`'s JSON string, so only each
 * document's name/size does — generation sends document input via blob
 * storage anyway, never the bytes.
 */
export type PostedCreateSlideConfig = Omit<CreateSlideConfig, "documents"> & {
  documents: Array<{ name: string; chars: number }>;
};

/**
 * One slide of the template plan the user approved on step 4: the brief the
 * outline produced plus the layout that will be filled with it. This is
 * exactly what POST /create-slide-template needs per slide, so the pane can
 * post it straight on without re-deriving anything.
 */
export interface TemplatePlanSlide {
  readonly slide_idx: number;
  readonly title: string;
  readonly subtitle?: string | null;
  readonly intent_tag: string;
  readonly main_points: string[];
  /** Catalog id, e.g. "standard-layouts/Cover_Hero.pptx#1". */
  readonly template_id: string;
  /** Whether this pick is the ranker's auto-choice or a manual override —
   *  display only, so the picker can badge rows it changed. */
  readonly autoPicked: boolean;
}

/** The whole approved plan, handed back to the pane on "Generate". */
export interface TemplatePlan {
  /** The outline's own request id, carried through so the fit call correlates
   *  with the outline that produced it in backend telemetry. */
  readonly requestId: string;
  readonly title: string;
  readonly slides: readonly TemplatePlanSlide[];
}

/** Per-slide lifecycle shown in the generation progress list. */
export type SlideGenStatus =
  | "waiting"
  | "picking"
  | "generating"
  | "done"
  | "error";

export interface SlidePlan {
  /** 1-based position in the deck. */
  index: number;
  title: string;
  status: SlideGenStatus;
  /** Sub-line, e.g. "Sketching the outline". */
  detail?: string;
}

/** Overall generation phase driving the progress view. */
export type GenerationPhase =
  | "idle"
  | "outlining"
  | "generating"
  | "done"
  | "cancelled"
  | "error";

/** Callbacks a generator emits as it works. */
export interface GeneratorCallbacks {
  onPlan: (plans: SlidePlan[]) => void;
  onSlideUpdate: (index: number, status: SlideGenStatus, detail?: string) => void;
  onProgress: (current: number, total: number) => void;
  onPhase: (phase: GenerationPhase) => void;
  onError: (message: string) => void;
}

/** Handle returned by a generation run so the caller can cancel it. */
export interface GenerationRun {
  cancel: () => void;
}

/**
 * Pluggable slide generator. Implemented by the backend client (apiGenerator),
 * which streams from POST /v1/deckorate/slides/create-slide-ai and drives the progress UI.
 */
export interface SlideGenerator {
  generate(config: CreateSlideConfig, callbacks: GeneratorCallbacks): GenerationRun;
}

/**
 * The templates-path counterpart, implemented by
 * service/templateGenerator.ts. It takes the plan the user approved on step 4
 * rather than the raw wizard config (the outline has already been generated
 * and each slide's layout chosen by then), fits each slide via
 * POST /create-slide-template, and patches + inserts the template slides.
 * Emits the SAME callbacks, so the progress UI is identical either way.
 */
export interface TemplateSlideGenerator {
  generate(plan: TemplatePlan, callbacks: GeneratorCallbacks): GenerationRun;
}
